import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import {
  Cog6ToothIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { settingsAPI } from '../services/api';
import { LoadingSpinner } from '../components/common';

export default function SettingsPage() {
  const queryClient = useQueryClient();
  const { register, handleSubmit, reset, formState: { errors } } = useForm();
  const [hasChanges, setHasChanges] = useState(false);

  const { data: settings, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: async () => {
      const response = await settingsAPI.get();
      return response.data;
    },
  });

  // Populate form when settings load
  React.useEffect(() => {
    if (settings) {
      console.log(settings);
      
      // Handle both response.data and response.data.data structures
      const settingsData = settings?.settings || settings;
      if (settingsData) {
        reset(settingsData);
      }
    }
  }, [settings, reset]);

  const updateMutation = useMutation({
    mutationFn: (data) => settingsAPI.update(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['settings']);
      toast.success('Settings updated successfully!');
      setHasChanges(false);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to update settings');
    },
  });

  const refreshMutation = useMutation({
    mutationFn: () => settingsAPI.refresh(),
    onSuccess: () => {
      queryClient.invalidateQueries(['settings']);
      toast.success('Settings cache refreshed!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to refresh cache');
    },
  });

  const onSubmit = (data) => {
    // Convert string values to integers for numeric fields
    const formattedData = {
      batch_default_size: parseInt(data.batch_default_size),
      non_winner_daily_deposit_cents: parseInt(data.non_winner_daily_deposit_cents),
      winner_daily_deposit_cents: parseInt(data.winner_daily_deposit_cents),
      winning_amount_cents: parseInt(data.winning_amount_cents),
      service_fee_cents: parseInt(data.service_fee_cents),
      penalty_fee_cents: parseInt(data.penalty_fee_cents),
      daily_number_of_winners: parseInt(data.daily_number_of_winners),
      daily_number_of_fixed_winners: parseInt(data.daily_number_of_fixed_winners),
      drawer_response_seconds: parseInt(data.drawer_response_seconds),
      max_draw_retry_drawers: parseInt(data.max_draw_retry_drawers),
      max_draw_attempts_per_candidate: parseInt(data.max_draw_attempts_per_candidate),
      seed_hmac_secret: data.seed_hmac_secret,
      lotto_number_length: parseInt(data.lotto_number_length),
      currency: data.currency,
      auto_mode_threshold_seconds: parseInt(data.auto_mode_threshold_seconds),
      login_phone_country_code: data.login_phone_country_code,
      grace_days_for_payment_status: parseInt(data.grace_days_for_payment_status),
    };
    updateMutation.mutate(formattedData);
  };

  const handleRefresh = () => {
    refreshMutation.mutate();
  };

  if (isLoading) {
    return <LoadingSpinner text="Loading settings..." />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">System Settings</h1>
          <p className="mt-2 text-sm text-gray-600">
            Configure system-wide settings and parameters
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshMutation.isLoading}
          className="btn-secondary flex items-center gap-2"
        >
          <ArrowPathIcon className={`h-5 w-5 ${refreshMutation.isLoading ? 'animate-spin' : ''}`} />
          Refresh Cache
        </button>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} onChange={() => setHasChanges(true)} className="space-y-6">
        {/* Batch Settings */}
        <div className="card">
          <div className="flex items-center gap-2 mb-4">
            <Cog6ToothIcon className="h-6 w-6 text-primary-600" />
            <h2 className="text-lg font-semibold text-gray-900">Batch Settings</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Batch Default Size *
              </label>
              <input
                {...register('batch_default_size', {
                  required: 'Batch default size is required',
                  min: { value: 1, message: 'Must be at least 1' }
                })}
                type="number"
                className="input-field"
                placeholder="100000"
              />
              {errors.batch_default_size && (
                <p className="mt-1 text-sm text-red-600">{errors.batch_default_size.message}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">Default number of slots per batch</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Service Fee (cents) *
              </label>
              <input
                {...register('service_fee_cents', {
                  required: 'Service fee is required',
                  min: { value: 0, message: 'Must be 0 or greater' }
                })}
                type="number"
                className="input-field"
                placeholder="500"
              />
              {errors.service_fee_cents && (
                <p className="mt-1 text-sm text-red-600">{errors.service_fee_cents.message}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">100 cents = 1 ETB</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Penalty Fee (cents) *
              </label>
              <input
                {...register('penalty_fee_cents', {
                  required: 'Penalty fee is required',
                  min: { value: 0, message: 'Must be 0 or greater' }
                })}
                type="number"
                className="input-field"
                placeholder="1000"
              />
              {errors.penalty_fee_cents && (
                <p className="mt-1 text-sm text-red-600">{errors.penalty_fee_cents.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Grace Days for Payment Status *
              </label>
              <input
                {...register('grace_days_for_payment_status', {
                  required: 'Grace days is required',
                  min: { value: 0, message: 'Must be 0 or greater' }
                })}
                type="number"
                className="input-field"
                placeholder="2"
              />
              {errors.grace_days_for_payment_status && (
                <p className="mt-1 text-sm text-red-600">{errors.grace_days_for_payment_status.message}</p>
              )}
            </div>
          </div>
        </div>

        {/* Deposit & Prize Settings */}
        <div className="card">
          <div className="flex items-center gap-2 mb-4">
            <Cog6ToothIcon className="h-6 w-6 text-primary-600" />
            <h2 className="text-lg font-semibold text-gray-900">Deposit & Prize Settings</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Non-Winner Daily Deposit (cents) *
              </label>
              <input
                {...register('non_winner_daily_deposit_cents', {
                  required: 'Non-winner daily deposit is required',
                  min: { value: 0, message: 'Must be 0 or greater' }
                })}
                type="number"
                className="input-field"
                placeholder="10000"
              />
              {errors.non_winner_daily_deposit_cents && (
                <p className="mt-1 text-sm text-red-600">{errors.non_winner_daily_deposit_cents.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Winner Daily Deposit (cents) *
              </label>
              <input
                {...register('winner_daily_deposit_cents', {
                  required: 'Winner daily deposit is required',
                  min: { value: 0, message: 'Must be 0 or greater' }
                })}
                type="number"
                className="input-field"
                placeholder="10000"
              />
              {errors.winner_daily_deposit_cents && (
                <p className="mt-1 text-sm text-red-600">{errors.winner_daily_deposit_cents.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Winning Amount (cents) *
              </label>
              <input
                {...register('winning_amount_cents', {
                  required: 'Winning amount is required',
                  min: { value: 0, message: 'Must be 0 or greater' }
                })}
                type="number"
                className="input-field"
                placeholder="1000000"
              />
              {errors.winning_amount_cents && (
                <p className="mt-1 text-sm text-red-600">{errors.winning_amount_cents.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Currency
              </label>
              <input
                {...register('currency')}
                type="text"
                className="input-field"
                placeholder="ETB"
              />
            </div>
          </div>
        </div>

        {/* Draw Settings */}
        <div className="card">
          <div className="flex items-center gap-2 mb-4">
            <Cog6ToothIcon className="h-6 w-6 text-primary-600" />
            <h2 className="text-lg font-semibold text-gray-900">Draw Settings</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Daily Number of Winners *
              </label>
              <input
                {...register('daily_number_of_winners', {
                  required: 'Daily number of winners is required',
                  min: { value: 1, message: 'Must be at least 1' }
                })}
                type="number"
                className="input-field"
                placeholder="10"
              />
              {errors.daily_number_of_winners && (
                <p className="mt-1 text-sm text-red-600">{errors.daily_number_of_winners.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Daily Number of Fixed Winners *
              </label>
              <input
                {...register('daily_number_of_fixed_winners', {
                  required: 'Daily number of fixed winners is required',
                  min: { value: 0, message: 'Must be 0 or greater' }
                })}
                type="number"
                className="input-field"
                placeholder="3"
              />
              {errors.daily_number_of_fixed_winners && (
                <p className="mt-1 text-sm text-red-600">{errors.daily_number_of_fixed_winners.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Drawer Response Seconds *
              </label>
              <input
                {...register('drawer_response_seconds', {
                  required: 'Drawer response seconds is required',
                  min: { value: 1, message: 'Must be at least 1' }
                })}
                type="number"
                className="input-field"
                placeholder="30"
              />
              {errors.drawer_response_seconds && (
                <p className="mt-1 text-sm text-red-600">{errors.drawer_response_seconds.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Draw Retry Drawers *
              </label>
              <input
                {...register('max_draw_retry_drawers', {
                  required: 'Max draw retry drawers is required',
                  min: { value: 1, message: 'Must be at least 1' }
                })}
                type="number"
                className="input-field"
                placeholder="5"
              />
              {errors.max_draw_retry_drawers && (
                <p className="mt-1 text-sm text-red-600">{errors.max_draw_retry_drawers.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Draw Attempts Per Candidate *
              </label>
              <input
                {...register('max_draw_attempts_per_candidate', {
                  required: 'Max draw attempts per candidate is required',
                  min: { value: 1, message: 'Must be at least 1' }
                })}
                type="number"
                className="input-field"
                placeholder="3"
              />
              {errors.max_draw_attempts_per_candidate && (
                <p className="mt-1 text-sm text-red-600">{errors.max_draw_attempts_per_candidate.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Auto Mode Threshold (seconds) *
              </label>
              <input
                {...register('auto_mode_threshold_seconds', {
                  required: 'Auto mode threshold is required',
                  min: { value: 1, message: 'Must be at least 1' }
                })}
                type="number"
                className="input-field"
                placeholder="60"
              />
              {errors.auto_mode_threshold_seconds && (
                <p className="mt-1 text-sm text-red-600">{errors.auto_mode_threshold_seconds.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Lotto Number Length *
              </label>
              <input
                {...register('lotto_number_length', {
                  required: 'Lotto number length is required',
                  min: { value: 1, message: 'Must be at least 1' }
                })}
                type="number"
                className="input-field"
                placeholder="10"
              />
              {errors.lotto_number_length && (
                <p className="mt-1 text-sm text-red-600">{errors.lotto_number_length.message}</p>
              )}
            </div>
          </div>
        </div>

        {/* System Settings */}
        <div className="card">
          <div className="flex items-center gap-2 mb-4">
            <Cog6ToothIcon className="h-6 w-6 text-primary-600" />
            <h2 className="text-lg font-semibold text-gray-900">System Settings</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Login Phone Country Code
              </label>
              <input
                {...register('login_phone_country_code')}
                type="text"
                className="input-field"
                placeholder="+251"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Seed HMAC Secret
              </label>
              <input
                {...register('seed_hmac_secret')}
                type="text"
                className="input-field"
                placeholder="CHANGE_THIS_IN_PRODUCTION"
              />
              <p className="mt-1 text-xs text-red-600">⚠️ Keep this secret secure!</p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 justify-end">
          <button
            type="button"
            onClick={() => {
              reset(settings);
              setHasChanges(false);
            }}
            className="btn-secondary"
            disabled={!hasChanges}
          >
            Reset Changes
          </button>
          <button
            type="submit"
            disabled={updateMutation.isLoading || !hasChanges}
            className="btn-primary"
          >
            {updateMutation.isLoading ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </form>

      {/* Info Box */}
      <div className="card bg-blue-50 border-blue-200">
        <div className="flex gap-3">
          <Cog6ToothIcon className="h-6 w-6 text-blue-600 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-blue-900 mb-1">Important Notes</h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• All amount fields are in cents (100 cents = 1 ETB)</li>
              <li>• Changes will affect new batches and draws</li>
              <li>• Existing batches will not be affected</li>
              <li>• Use "Refresh Cache" to apply changes immediately</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
